import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getActorCredits, getActorDetails } from './API';
import { Box, Card, CardContent, CardMedia, Typography, Button, Skeleton, Stack, useMediaQuery } from '@mui/material';
import LoadingSpinner from './LoadingSpinner';
import { useTheme } from '@mui/system';

function ActorDetails() {
    const { id } = useParams();
    const [actor, setActor] = useState(null);
    const [credits, setCredits] = useState({ cast: [], crew: [] });
    const [showAllCredits, setShowAllCredits] = useState(false);
    const navigate = useNavigate();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchActor = async () => {
            try {
                const data = await getActorDetails(id);
                setActor(data);
            } catch (error) {
                console.error("Erreur lors de la recherche de l'acteur:", error);
            }
        };

        const fetchActorCredits = async () => {
            try {
                const data = await getActorCredits(id);
                setCredits(data);
            } catch (error) {
                console.error("Erreur lors de la recherche des crédits de l'acteur:", error);
            }
        };

        fetchActor();
        fetchActorCredits();
    }, [id]);

    if (!actor) {
        return <LoadingSpinner size={60} color="secondary" text="Chargement des données..." />;
    }

    const convertDate = (date) => {
        const [year, month, day] = date.split('-');
        return `${day}/${month}/${year}`;
    };

    const calculateAge = (date) => {
        const [year, month, day] = date.split('-');
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const navToKdramaDetails = (id) => {
        navigate(`/kdrama/${id}`);
    };

    const displayedCredits = showAllCredits ? credits.cast : credits.cast.slice(0, 8);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: isMobile ? '1rem' : '2rem' }}>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start', width: '100%', gap: isMobile ? 2 : 4 }}>
                {/* Section de l'image et des infos personnelles */}
                <Box
                    sx={{
                        flex: '0 0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',  // Centrer le contenu
                        gap: 2,
                        maxWidth: isMobile ? '200px' : '300px',  // Ajustement de la largeur sur mobile
                        width: '100%',
                        margin: isMobile ? '0 auto' : 'initial',  // Centrage horizontal sur mobile
                        position: isMobile ? 'static' : 'sticky',
                        top: '2rem',
                    }}
                >
                    {actor.profile_path ? (
                        <img
                            src={`https://media.themoviedb.org/t/p/w300_and_h450_bestv2/${actor.profile_path}`}
                            alt={actor.name}
                            style={{
                                borderRadius: '10px',
                                width: '100%',
                                maxWidth: '100%',
                            }}
                        />
                    ) : (
                        <Skeleton variant="rectangular" width="100%" height={450} />
                    )}
                    <Stack spacing={1} sx={{ textAlign: 'center', maxWidth: '100%' }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontFamily: 'SUSE, sans-serif' }}>Informations personnelles :</Typography>
                        <Typography variant="body2" sx={{ color: 'white', fontFamily: 'SUSE, sans-serif' }}><strong>Connu pour :</strong> {actor?.known_for_department || 'Non disponible'}</Typography>
                        <Typography variant="body2" sx={{ color: 'white', fontFamily: 'SUSE, sans-serif' }}><strong>Lieu de naissance :</strong> {actor?.place_of_birth || 'Non disponible'}</Typography>
                        <Typography variant="body2" sx={{ color: 'white', fontFamily: 'SUSE, sans-serif' }}><strong>Date de naissance :</strong> {actor?.birthday ? convertDate(actor?.birthday) : 'Non disponible'} ({calculateAge(actor?.birthday) || 'Non disponible'} ans)</Typography>
                        {actor?.deathday && (
                            <Typography variant="body2" sx={{ color: 'white' }}><strong>Date de décès :</strong> {actor?.deathday}</Typography>
                        )}
                    </Stack>
                </Box>

                {/* Section de la biographie et des crédits */}
                <Box sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', fontFamily: 'SUSE, sans-serif' }}>
                        {actor?.name}
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white', fontFamily: 'SUSE, sans-serif' }}><strong>Biographie :</strong></Typography>
                    <Typography variant="body1" sx={{ color: 'white', fontSize: '1.1rem', lineHeight: 1.5, textAlign: 'justify', fontFamily: 'SUSE, sans-serif' }}>
                        {actor?.biography || 'Biographie non disponible.'}
                    </Typography>

                    {/* Section des crédits */}
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', marginBottom: '0.5rem', fontFamily: 'SUSE, sans-serif' }}>A joué aussi dans :</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? '0.5rem' : '1rem', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                            {displayedCredits.sort((a, b) => b.popularity - a.popularity).map((credit) => (
                                <Card
                                    onClick={() => navToKdramaDetails(credit.id)}
                                    key={credit.id}
                                    sx={{
                                        width: isMobile ? 120 : 150,
                                        padding: '0.5rem',
                                        cursor: 'pointer',
                                        boxShadow: 3,
                                        transition: 'transform 0.3s, box-shadow 0.3s',
                                        '&:hover': {
                                            transform: isMobile ? 'none' : 'scale(1.05)',
                                            boxShadow: 6
                                        },
                                        borderRadius: 2,
                                        overflow: 'hidden'
                                    }}
                                >
                                    {credit.poster_path ? (
                                        <CardMedia
                                            sx={{ height: 225, objectFit: 'cover' }}
                                            component={'img'}
                                            image={`https://image.tmdb.org/t/p/w500/${credit.poster_path}`}
                                            alt={credit?.name || 'Image non disponible'}
                                        />
                                    ) : (
                                        <Skeleton variant="rectangular" width="100%" height={225} />
                                    )}
                                    <CardContent>
                                        <Typography variant="body2" sx={{ textAlign: 'center', fontFamily: 'SUSE, sans-serif' }}>{credit?.name}</Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                        {credits.cast.length > 8 && (
                            <Button onClick={() => setShowAllCredits(!showAllCredits)} sx={{ marginTop: '1rem', alignSelf: 'center', color: 'white', backgroundColor: '#2596BE' }}>
                                {showAllCredits ? 'Voir moins' : 'Voir plus'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default ActorDetails;
